import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, ShieldCheck, Loader2, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { ArcheIconImg } from '@/lib/arche-icon-url';
import type { ArcheDisplayMeta } from '@/lib/api';
import { useFavoriteStatus } from '../hooks/useFavoriteStatus';
import type { FeaturedArche } from '../data/featured-arches';
import { SecurityReviewDialog } from './SecurityReviewDialog';
import { ReplacesList } from './ReplacesList';

interface FeaturedArcheRowProps {
  arche: FeaturedArche;
  /** Live metadata (real icon URL); undefined while loading or if unpublished. */
  meta?: ArcheDisplayMeta;
}

/**
 * One available featured arche, as a table row.
 *
 * Responsive: at md+ it's a five-column row (product / what it does / replaces
 * / security / worktop). Below md the table narrows to two columns — Product
 * and the emphasized Replaces — so nothing gets cut off; the description, the
 * Add-to-Worktop button, and the Security link fold into the Product cell.
 */
export function FeaturedArcheRow({ arche, meta }: FeaturedArcheRowProps) {
  const navigate = useNavigate();
  const { isFavorite, isLoading, toggle } = useFavoriteStatus(arche.archId);
  const [securityOpen, setSecurityOpen] = useState(false);
  const FallbackIcon = arche.icon;

  // Defined once, rendered in the product cell (mobile) and in their own
  // columns (md+); CSS hides whichever copy doesn't apply at the breakpoint.
  const addButton = (
    <Button
      variant={isFavorite ? 'secondary' : 'default'}
      size="sm"
      className="gap-1.5"
      disabled={isLoading}
      onClick={() => toggle()}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isFavorite ? (
        <Check className="h-4 w-4" />
      ) : (
        <Plus className="h-4 w-4" />
      )}
      {isFavorite ? 'Added' : 'Add'}
    </Button>
  );

  const securityButton = (
    <Button
      variant="ghost"
      size="sm"
      className="gap-1.5 text-muted-foreground"
      onClick={() => setSecurityOpen(true)}
    >
      <ShieldCheck className="h-4 w-4" />
      Review
    </Button>
  );

  return (
    <>
      <TableRow>
        {/* Product — icon + name; on mobile also holds description + actions */}
        <TableCell className="align-top md:align-middle">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => navigate(`/a/${arche.archId}`)}
              className="group flex items-center gap-2.5 text-left"
            >
              {meta?.imageRef ? (
                <ArcheIconImg
                  imageRef={meta.imageRef}
                  size={64}
                  alt={arche.name}
                  className="h-9 w-9 flex-shrink-0 rounded-lg object-cover shadow-sm"
                />
              ) : (
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 text-white shadow-sm">
                  <FallbackIcon className="h-5 w-5" />
                </span>
              )}
              <span className="font-semibold text-foreground group-hover:underline">
                {arche.name}
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </button>

            {/* Mobile-only: fold the description + actions into the product cell */}
            <div className="flex flex-col gap-2 md:hidden">
              <p className="line-clamp-2 text-sm text-muted-foreground">{arche.description}</p>
              <div className="flex flex-wrap items-center gap-2">
                {addButton}
                {securityButton}
              </div>
            </div>
          </div>
        </TableCell>

        {/* What it does — md+ only */}
        <TableCell className="hidden max-w-xs align-middle text-sm text-muted-foreground md:table-cell">
          {arche.description}
        </TableCell>

        {/* Replaces — always visible, emphasized */}
        <TableCell className="bg-primary/[0.04] align-middle">
          <ReplacesList items={arche.compareWith} />
        </TableCell>

        {/* Security review — md+ only */}
        <TableCell className="hidden align-middle md:table-cell">{securityButton}</TableCell>

        {/* Add to worktop — md+ only */}
        <TableCell className="hidden align-middle text-right md:table-cell">{addButton}</TableCell>
      </TableRow>

      <SecurityReviewDialog
        open={securityOpen}
        onOpenChange={setSecurityOpen}
        archeName={arche.name}
        securityNote={arche.securityNote}
      />
    </>
  );
}
